/**
 * The network menu.
 *
 * «Грати разом» is not a third detail panel beside Керування and Збереження -
 * it replaces the left-hand column. That is deliberate and it is what the
 * layout is saying: the other two are things you look at *about this game*,
 * and going online is somewhere else. You come back from it, which is why it
 * has a way back and the others do not.
 *
 * Two ways in: join a world that is there, or make one. Both now end in
 * pressing a button and being in a game.
 *
 * **There is no address field, and no command to type.** Creating used to
 * hand out `npm run server` and leave you to it, because a browser tab
 * cannot listen on a port - which is true, and was never the player's
 * problem. The server makes the world now, so «Почати гру» starts a game.
 * And the server is the machine that served this page: typing its address
 * into a box on a page it served was asking somebody to tell a room where
 * they are standing.
 *
 * DOM only. What an address *is* still lives in core/net/address.ts.
 */

import { suggestedAddress } from "../core/net/address.js";
import { MAPS, mapById } from "../core/world/maps.js";
import { MATCH_HOURS, MAX_MATCH_HOURS } from "../core/net/match.js";
import { PROTOCOL_VERSION } from "../core/net/protocol.js";
import {
  savedName, rememberName, savedPassword, rememberPassword,
  signedInAs, signOut, keepNameTag,
} from "../platform/net.js";
import { ui, pick, type Lang, type UiKey, type Phrase } from "../core/text/lang.js";

/** What the screen is showing. */
export type NetworkPane = "join" | "create";

export interface NetworkActions {
  /**
   * Knock on a server and see whether it answers.
   *
   * Separated from the panel so a test can drive the screen without a socket,
   * and because knocking is worth doing before joining: it turns "the page
   * reloaded and nothing happened" into a sentence about what went wrong.
   */
  probe: (url: string, word: string, world?: string) => Promise<ProbeAnswer>;
  /** Leave the network menu and put the ordinary home screen back. */
  back: () => void;
  /**
   * Actually go and play there.
   *
   * Separate from `probe` because finding a server and joining it are
   * different acts with different consequences - one is a question and the
   * other reloads the page into somebody else's world.
   */
  join: (
    url: string, word: string, world: string,
    /** On somebody's own computer, which is a different way in. */
    guest?: boolean,
  ) => void;
  /**
   * Ask the account desk something: register, forgot, reset.
   *
   * Separate from `probe` and from `join` because none of them is about a
   * world - they happen before there is any question of which one, and none
   * needs the word.
   */
  account: (url: string, message: object) =>
    Promise<{ ok: boolean; why: string; token?: string }>;
  /**
   * Hold a world of this map on this computer, and walk into it.
   *
   * Reloads, like joining does and for the same reason - the world is grown
   * at startup - which is also why nothing is made here: a world made on
   * this screen would die on the way to itself. What crosses the reload is
   * the intention.
   */
  hold: (url: string, map: string, word: string,
    match?: { bots: boolean; prep: number; hardcore: boolean; limit: number }) => void;
  /**
   * Whether the server would take a world held here under this name -
   * asked before `hold`, so that "no account" is a line under the button
   * rather than a reload that comes back to the home screen. Answers with
   * the ui key of the refusal.
   */
  mayHost: (url: string, name: string, password: string) =>
    Promise<{ ok: boolean; why: string }>;
  /**
   * Is there a world of theirs already written down in this browser?
   *
   * Asked as soon as the making side is opened, so the screen can say "your
   * кремль is here, you played it on Tuesday" rather than making somebody
   * press a button to find out whether anything happens. Null where there
   * is nothing, or where the browser will not say.
   */
  kept: (owner: string) => Promise<{ map: string; savedAt: number } | null>;
  /** Delete the world kept under that name, so the next one starts fresh. */
  forget: (owner: string) => Promise<void>;
}

/** What a knock came back with. Mirrors platform/netProbe's ProbeResult. */
export interface ProbeAnswer {
  readonly verdict:
    | "ok" | "mismatch" | "wrong-word" | "unreachable"
    | "gone" | "full" | "late" | "name-taken";
  readonly worlds: readonly {
    readonly id: string; readonly name: Phrase; readonly blurb: Phrase;
    readonly players: number;
    /** Whether a word will be asked for. Never the word itself. */
    readonly locked?: boolean;
    readonly owner?: string;
    /**
     * This world is on a player's computer, not the one serving this page.
     *
     * What it changes: going in means being introduced to that machine and
     * talking to it directly, rather than walking in here. It is also why
     * the row says whose computer it is - see the list.
     */
    readonly guest?: true;
  }[];
}

export class NetworkUI {
  /** Set by main.ts. Call `retitle` after changing it. */
  lang: Lang = "uk";

  private pane: NetworkPane = "join";
  private readonly menuEl: HTMLDivElement;
  private readonly detailEl: HTMLDivElement;
  private readonly backButton: HTMLButtonElement;
  private readonly joinButton: HTMLButtonElement;
  private readonly createButton: HTMLButtonElement;
  /** Where the worlds are: the machine that served this page. */
  private readonly url: string;
  /** The map a new world would be made of. */
  private readonly mapSelect: HTMLSelectElement;
  private readonly mapRow: HTMLDivElement;
  /** «Сучасна війна» only: bots, and how long the preparing lasts. */
  private readonly warRow: HTMLDivElement;
  private readonly botsLabel: HTMLLabelElement;
  private readonly botsToggle: HTMLInputElement;
  private readonly prepLabel: HTMLLabelElement;
  private readonly prepInput: HTMLInputElement;
  private readonly botsText: HTMLSpanElement;
  /** How many hours the match lasts before the world goes. */
  private readonly hoursLabel: HTMLLabelElement;
  private readonly hoursInput: HTMLInputElement;
  private readonly hoursText: HTMLSpanElement;
  /** Хардкор: ten скринь of supplies by every flag. */
  private readonly hardcoreLabel: HTMLLabelElement;
  private readonly hardcoreToggle: HTMLInputElement;
  private readonly hardcoreText: HTMLSpanElement;
  private readonly prepText: HTMLSpanElement;
  private wordRow!: HTMLDivElement;
  /** The world a padlock was clicked on, waiting for its word. */
  private chosen: { id: string; guest: boolean } | null = null;
  /**
   * The world this browser is already holding for them, if any.
   *
   * Looked up by the name in the field, because that is what a held world
   * is filed under - one account, one world - and it changes as they type.
   */
  private keptWorld: { map: string; savedAt: number } | null = null;
  private readonly connectButton: HTMLButtonElement;
  private readonly againButton: HTMLButtonElement;
  private readonly statusEl: HTMLDivElement;
  private readonly blurbEl: HTMLDivElement;
  private readonly noteEl: HTMLDivElement;
  private readonly wordInput: HTMLInputElement;
  private readonly nameInput: HTMLInputElement;
  private readonly passInput: HTMLInputElement;
  private readonly emailInput: HTMLInputElement;
  private readonly emailRow: HTMLDivElement;
  private readonly emailNote: HTMLDivElement;
  private readonly registerButton: HTMLButtonElement;
  private readonly forgotButton: HTMLButtonElement;
  private readonly nameRow: HTMLDivElement;
  private readonly passRow: HTMLDivElement;
  private readonly accountRow: HTMLDivElement;
  /**
   * Signing in, apart from making a world - "як працює логін: прибираємо
   * поля логіну з форми гри, це різні речі". The create pane shows who is
   * signed in, or that nobody is and a button; the button opens a dialog
   * with the fields, and «Створити обліковку» and «Забув гасло» live there.
   * Signed in, the dialog closes, the name shows, and «Почати гру» wakes.
   */
  private readonly signRow: HTMLDivElement;
  private readonly signText: HTMLSpanElement;
  private readonly signName: HTMLElement;
  private readonly signButton: HTMLButtonElement;
  private readonly dialogEl: HTMLDivElement;
  private readonly dialogTitle: HTMLDivElement;
  private readonly dialogName: HTMLInputElement;
  private readonly dialogPass: HTMLInputElement;
  private readonly dialogEmailRow: HTMLDivElement;
  private readonly dialogEmail: HTMLInputElement;
  private readonly dialogEmailNote: HTMLDivElement;
  private readonly dialogStatus: HTMLDivElement;
  private readonly dialogGo: HTMLButtonElement;
  private readonly dialogRegister: HTMLButtonElement;
  private readonly dialogForgot: HTMLButtonElement;
  private readonly dialogCancel: HTMLButtonElement;
  private dialogMaking = false;
  private dialogSaid: UiKey | null = null;
  private signedName = "";
  /** True once «Створити обліковку» has been pressed and is now «Створити». */
  private making = false;
  private readonly worldsEl: HTMLDivElement;
  private busy = false;

  constructor(
    menu: HTMLDivElement,
    detail: HTMLDivElement,
    private readonly actions: NetworkActions,
    pageHost: string,
    pageSecure: boolean,
  ) {
    this.menuEl = menu;
    this.detailEl = detail;

    // Clicks here are the player reaching for a control, never a request to
    // start playing - the overlay turns any stray click into pointer lock.
    for (const el of [menu, detail]) {
      el.addEventListener("click", (e) => e.stopPropagation());
      el.addEventListener("mousedown", (e) => e.stopPropagation());
    }

    this.backButton = button("home-tab", () => this.actions.back());
    this.joinButton = button("home-tab", () => this.show("join"));
    this.createButton = button("home-tab", () => this.show("create"));
    menu.append(this.backButton, this.joinButton, this.createButton);

    this.blurbEl = document.createElement("div");
    this.blurbEl.className = "net-blurb";

    /*
     * The machine that served this page, and no field to say so.
     *
     * There used to be a box to type an address into, filled in with this
     * same value, and a sentence telling somebody to enter the address they
     * were given. Both are gone: the page came from a server, the server is
     * the one with the worlds on it, and asking somebody to tell a room
     * where they are standing is not a question worth asking.
     */
    this.url = suggestedAddress(pageHost, pageSecure);

    const row = document.createElement("div");
    row.className = "save-maker";
    this.connectButton = button("save-button primary", () => void this.connect());
    // Beside «Продовжити», when there is something to continue.
    this.againButton = button("save-button danger", () => void this.startAgain());
    this.againButton.hidden = true;
    row.append(this.connectButton, this.againButton);

    /*
     * The word the host hands out.
     *
     * Its own row under the address, because they come from different places:
     * the address is a machine and the word is a person telling you it. A
     * text field rather than a password one - it is not a secret worth
     * hiding from the room, and hiding it only makes it harder to check what
     * you typed.
     */
    /*
     * Who you are, above the word.
     *
     * Required, and it was not there at all before: what travelled as a name
     * was the *skin's* name, so everybody who picked the same козак was
     * called the same thing - two players in one world, both «Силач», two
     * name tags saying it, and one bag between them.
     */
    const nameRow = document.createElement("div");
    this.nameRow = nameRow;
    nameRow.className = "save-maker";
    this.nameInput = document.createElement("input");
    this.nameInput.type = "text";
    this.nameInput.className = "save-name";
    this.nameInput.maxLength = 24;
    this.nameInput.value = savedName();
    this.nameInput.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") void this.connect();
    });
    nameRow.append(this.nameInput);

    /*
     * The password, which is optional and says so.
     *
     * Empty means a guest: a name nobody registered, held by the tag this
     * browser keeps. Somebody who only wants to play never has to fill this
     * in, which is the whole reason logging in is optional.
     */
    const passRow = document.createElement("div");
    this.passRow = passRow;
    passRow.className = "save-maker";
    this.passInput = document.createElement("input");
    this.passInput.type = "password";
    this.passInput.className = "save-name";
    this.passInput.value = savedPassword();
    this.passInput.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") void this.connect();
    });
    passRow.append(this.passInput);

    /*
     * An address, and the only reason to give one.
     *
     * Hidden until somebody is making an account, because it is the only
     * moment it means anything - and said at the field rather than in a page
     * nobody reads, because that is where the decision is being made.
     */
    this.emailRow = document.createElement("div");
    this.emailRow.className = "save-maker";
    this.emailRow.hidden = true;
    this.emailInput = document.createElement("input");
    this.emailInput.type = "email";
    this.emailInput.className = "save-name";
    this.emailInput.addEventListener("keydown", (e) => e.stopPropagation());
    this.emailRow.append(this.emailInput);
    this.emailNote = document.createElement("div");
    this.emailNote.className = "net-note";
    this.emailNote.hidden = true;

    // Two small ways out of "I have no account" and "I have forgotten it".
    const accountRow = document.createElement("div");
    this.accountRow = accountRow;
    accountRow.className = "net-account-row";
    this.registerButton = button("net-small", () => void this.register());
    this.forgotButton = button("net-small", () => void this.forgot());
    accountRow.append(this.registerButton, this.forgotButton);

    /*
     * Which map a new world is made of.
     *
     * Only on the making side: joining is choosing from what is there, and
     * what is there is a list with names on it already.
     */
    // Who is signed in, on the create pane, and the way in when nobody is.
    this.signRow = document.createElement("div");
    this.signRow.className = "net-sign";
    this.signText = document.createElement("span");
    this.signName = document.createElement("b");
    // Its own class, not `net-small`: the join pane's two small buttons are
    // found by that name, first and second, and this one comes before them.
    this.signButton = button("net-sign-button", () => {
      if (this.signedName.length > 0) this.signOutNow(); else this.openLogin();
    });
    this.signRow.append(this.signText, this.signName, this.signButton);

    // The dialog, over everything, with its own fields.
    this.dialogEl = document.createElement("div");
    this.dialogEl.className = "net-dialog";
    this.dialogEl.id = "net-dialog";
    this.dialogEl.hidden = true;
    const box = document.createElement("div");
    box.className = "net-dialog-box";
    this.dialogTitle = document.createElement("div");
    this.dialogTitle.className = "net-dialog-title";
    const stopKeys = (input: HTMLInputElement, onEnter: () => void): void => {
      input.addEventListener("keydown", (e) => {
        e.stopPropagation();
        if (e.key === "Enter") onEnter();
        if (e.key === "Escape") this.closeLogin();
      });
    };
    const field = (type: string): [HTMLDivElement, HTMLInputElement] => {
      const wrap = document.createElement("div");
      wrap.className = "save-maker";
      const input = document.createElement("input");
      input.type = type;
      input.className = "save-name";
      if (type === "text") input.maxLength = 24;
      stopKeys(input, () => void this.dialogGoNow());
      wrap.append(input);
      return [wrap, input];
    };
    const [dNameRow, dName] = field("text");
    const [dPassRow, dPass] = field("password");
    const [dEmailRow, dEmail] = field("email");
    this.dialogName = dName;
    this.dialogPass = dPass;
    this.dialogEmailRow = dEmailRow;
    this.dialogEmail = dEmail;
    this.dialogEmailRow.hidden = true;
    this.dialogEmailNote = document.createElement("div");
    this.dialogEmailNote.className = "net-note";
    this.dialogEmailNote.hidden = true;
    this.dialogStatus = document.createElement("div");
    this.dialogStatus.className = "net-status";
    this.dialogStatus.hidden = true;
    const goRow = document.createElement("div");
    goRow.className = "save-maker";
    this.dialogGo = button("save-button primary", () => void this.dialogGoNow());
    goRow.append(this.dialogGo);
    const smallRow = document.createElement("div");
    smallRow.className = "net-account-row";
    this.dialogRegister = button("net-small", () => this.dialogRegisterMode(!this.dialogMaking));
    this.dialogForgot = button("net-small", () => void this.dialogForgotNow());
    this.dialogCancel = button("net-small", () => this.closeLogin());
    smallRow.append(this.dialogRegister, this.dialogForgot, this.dialogCancel);
    box.append(this.dialogTitle, dNameRow, dPassRow, this.dialogEmailRow,
      this.dialogEmailNote, this.dialogStatus, goRow, smallRow);
    this.dialogEl.append(box);
    for (const el of [this.dialogEl]) {
      el.addEventListener("click", (e) => { e.stopPropagation(); if (e.target === el) this.closeLogin(); });
      el.addEventListener("mousedown", (e) => e.stopPropagation());
    }
    document.body.append(this.dialogEl);

    this.mapRow = document.createElement("div");
    this.mapRow.className = "save-maker";
    this.mapSelect = document.createElement("select");
    this.mapSelect.className = "pick-select";
    for (const map of MAPS) {
      const option = document.createElement("option");
      option.value = map.id;
      option.textContent = pick(map.name, this.lang);
      this.mapSelect.append(option);
    }
    this.mapSelect.addEventListener("click", (e) => e.stopPropagation());
    this.mapSelect.addEventListener("change", () => this.draw());
    this.mapRow.append(this.mapSelect);

    /*
     * And the two things «Сучасна війна» is made with.
     *
     * "Той хто створює гру вибирає чекбоксом «проти ботів»", and "час на
     * підготовку задає створювач гри, за замовчуванням це 10 хвилин". Both
     * belong to the act of making the world rather than to the lobby: they
     * decide what match this is, and a match that could change shape while
     * people were joining it would be two matches with one list of players.
     *
     * Hidden on every other map, because on every other map they mean
     * nothing at all.
     */
    this.warRow = document.createElement("div");
    this.warRow.className = "save-maker";
    this.botsLabel = document.createElement("label");
    /*
     * A class of its own rather than the recipe book's `book-only`.
     *
     * They look the same and they are not the same thing, and `check:adventure`
     * clicks "the first .book-only input" to fold the book - which, with
     * these two wearing that class, was a hidden checkbox on the network
     * panel. It timed out waiting for it to become visible.
     */
    this.botsLabel.className = "war-setting";
    this.botsToggle = document.createElement("input");
    this.botsToggle.type = "checkbox";
    this.botsToggle.id = "net-bots";
    this.botsText = document.createElement("span");
    this.botsLabel.append(this.botsToggle, this.botsText);
    this.prepInput = document.createElement("input");
    this.prepInput.type = "number";
    /*
     * No `save-name` class on this one, deliberately.
     *
     * Every other text field on this panel wears it, and `check:ui` reaches
     * for "the first .save-name in the detail" when it types a name - so a
     * hidden field of that class sitting above the name field is a field
     * the check types into and cannot see. It timed out on exactly that.
     */
    this.prepInput.id = "net-prep";
    this.prepInput.min = "1";
    this.prepInput.max = "60";
    this.prepInput.value = "10";
    this.prepInput.addEventListener("keydown", (e) => e.stopPropagation());
    this.prepLabel = document.createElement("label");
    this.prepLabel.className = "war-setting";
    this.prepText = document.createElement("span");
    this.prepLabel.append(this.prepInput, this.prepText);
    this.hardcoreLabel = document.createElement("label");
    this.hardcoreLabel.className = "war-setting";
    this.hardcoreToggle = document.createElement("input");
    this.hardcoreToggle.type = "checkbox";
    this.hardcoreToggle.id = "net-hardcore";
    this.hardcoreText = document.createElement("span");
    this.hardcoreLabel.append(this.hardcoreToggle, this.hardcoreText);
    this.hoursInput = document.createElement("input");
    this.hoursInput.type = "number";
    this.hoursInput.id = "net-hours";
    this.hoursInput.min = "1";
    this.hoursInput.max = String(MAX_MATCH_HOURS);
    this.hoursInput.value = String(MATCH_HOURS);
    this.hoursInput.addEventListener("keydown", (e) => e.stopPropagation());
    this.hoursLabel = document.createElement("label");
    this.hoursLabel.className = "war-setting";
    this.hoursText = document.createElement("span");
    this.hoursLabel.append(this.hoursInput, this.hoursText);
    this.warRow.append(this.botsLabel, this.prepLabel, this.hoursLabel, this.hardcoreLabel);

    this.wordRow = document.createElement("div");
    this.wordRow.className = "save-maker";
    const wordRow = this.wordRow;
    this.wordInput = document.createElement("input");
    this.wordInput.type = "text";
    this.wordInput.className = "save-name";
    this.wordInput.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key !== "Enter") return;
      // A word typed for a world that was clicked goes to that world, not
      // back to the list: being sent round the loop again after typing the
      // thing you were asked for is the screen not listening.
      const waiting = this.chosen;
      if (waiting !== null && this.pane === "join") {
        this.chosen = null;
        void this.enter(this.address(), waiting.id, waiting.guest);
        return;
      }
      void this.connect();
    });
    wordRow.append(this.wordInput);

    this.statusEl = document.createElement("div");
    this.statusEl.className = "net-status";
    this.statusEl.hidden = true;

    /*
     * Which world, when the server has more than one.
     *
     * Filled in after a knock, because only the server knows what it is
     * offering - and a list written into the page would be a list of what
     * some other server had. With one world there is nothing to choose and
     * this stays empty: a menu with a single item is a step, not a choice.
     */
    this.worldsEl = document.createElement("div");
    this.worldsEl.className = "net-worlds";
    this.worldsEl.hidden = true;

    /*
     * The command is a button, because people click it.
     *
     * It sat here as a bordered box of monospace text with a border round it,
     * which is what every button on this screen looks like - so it was
     * clicked, and nothing happened, and neither did the game, because clicks
     * inside this menu are deliberately kept away from the overlay. A thing
     * that looks pressable has to do something when pressed.
     *
     * What it does is copy itself. It cannot start a server - a browser tab
     * cannot spawn a process - so the most it can honestly offer is to save
     * you retyping it into a terminal.
     */
    this.noteEl = document.createElement("div");
    this.noteEl.className = "net-note";

    detail.append(this.blurbEl, this.mapRow, this.warRow, this.signRow, nameRow, passRow,
      this.emailRow,
      this.emailNote, accountRow, wordRow, this.statusEl,
      this.worldsEl, row, this.noteEl);
    this.retitle();
    /*
     * Laid out, not knocked on.
     *
     * The panel is built while the page loads and is not on the screen yet;
     * knocking here would be a socket nobody asked for, and its answer would
     * land a second later on top of whatever the page had decided to say by
     * then - which is exactly what it did to the sentence a reset link puts
     * up.
     */
    this.show("join", false);
  }

  /**
   * Somebody followed the link out of a reset message.
   *
   * The ticket is in the address, so the page can be opened on any machine -
   * which is the point of a reset, and why it is not tied to the browser
   * that asked. The panel opens on the join pane with the password field
   * waiting, and pressing sends the new one to the server that wrote.
   */
  openReset(ticket: string): void {
    this.ticket = ticket;
    this.show("join");
    this.registerButton.hidden = true;
    this.forgotButton.hidden = true;
    this.connectButton.textContent = ui("net-reset-do", this.lang);
    this.say("net-reset-title", "working");
    this.passInput.value = "";
    this.passInput.focus();
  }

  /**
   * Open straight onto making an account.
   *
   * Registering was three screens in: «Грати разом», then the join pane,
   * then a small button under the password. Somebody who has never played
   * with anybody has no reason to look there - "додай кнопку реєстрації на
   * головний екран" - so the home screen asks for it directly and this is
   * what it asks for.
   *
   * The same state the small button's first press puts the panel into: the
   * email row out, the button reading «Створити», and the cursor in the name.
   * A second press then sends it, so there is one way of registering and not
   * two.
   */
  /**
   * Back from a join that was refused: the join pane, with the reason
   * standing on it once the list has come back - the knock the pane opens
   * with would otherwise write "pick a world" over it.
   */
  openTrouble(key: UiKey): void {
    this.trouble = key;
    this.show("join");
    this.say(key, "bad");
  }

  private trouble: UiKey | null = null;

  openRegister(): void {
    this.show("create");
    this.openLogin();
    this.dialogRegisterMode(true);
  }

  /**
   * Show or hide the whole thing.
   *
   * Opening it knocks, which is the whole of a bug worth writing down.
   * `show` knocks when somebody *switches* pane, and the panel is built on
   * the joining pane at page load with the knock deliberately skipped - a
   * socket nobody asked for while the page is still loading. Between those
   * two, nothing ever knocked when the panel was simply opened: pressing
   * «Грати разом» showed an empty space where the worlds go, and the list
   * appeared only after a trip through «Створити» and back, or a press of
   * «Оновити список». Reported as exactly that: "нажимаю створити і потім
   * назад приєднатись і скрін вже інший".
   *
   * Opening the panel *is* somebody asking, which is the difference between
   * this and the one in the constructor.
   */
  setOpen(open: boolean): void {
    this.menuEl.hidden = !open;
    this.detailEl.hidden = !open;
    if (!open) return;
    // Not over a reset message, and not on top of a knock already going.
    if (this.pane !== "join" || this.ticket !== null || this.busy) return;
    void this.connect();
  }

  /** Write every caption again, in the language now in force. */
  retitle(): void {
    this.backButton.textContent = ui("net-back", this.lang);
    this.joinButton.textContent = ui("net-join", this.lang);
    this.createButton.textContent = ui("net-create", this.lang);
    this.connectButton.textContent = ui("net-connect", this.lang);
    this.nameInput.placeholder = ui("net-name", this.lang);
    this.passInput.placeholder = ui("net-password", this.lang);
    this.emailInput.placeholder = ui("net-email", this.lang);
    this.emailNote.textContent = ui("net-email-note", this.lang);
    this.registerButton.textContent =
      ui(this.making ? "net-register-do" : "net-register", this.lang);
    this.forgotButton.textContent = ui("net-forgot", this.lang);
    this.wordInput.placeholder = ui("net-word", this.lang);
    this.botsText.textContent = ui("net-bots", this.lang);
    this.hardcoreText.textContent = ui("net-hardcore", this.lang);
    this.hoursText.textContent = ui("net-hours", this.lang);
    this.prepText.textContent = ui("net-prep", this.lang);
    this.dialogTitle.textContent = ui(this.dialogMaking ? "net-register" : "net-login-title", this.lang);
    this.dialogName.placeholder = ui("net-name", this.lang);
    this.dialogPass.placeholder = ui("net-password", this.lang);
    this.dialogEmail.placeholder = ui("net-email", this.lang);
    this.dialogEmailNote.textContent = ui("net-email-note", this.lang);
    this.dialogGo.textContent = ui(this.dialogMaking ? "net-register-do" : "net-login", this.lang);
    this.dialogRegister.textContent = ui(this.dialogMaking ? "net-login-title" : "net-register", this.lang);
    this.dialogForgot.textContent = ui("net-forgot", this.lang);
    this.dialogCancel.textContent = ui("net-cancel", this.lang);
    if (this.dialogSaid !== null) this.dialogStatus.textContent = ui(this.dialogSaid, this.lang);
    const standing = this.said;
    if (standing !== null && !this.statusEl.hidden) {
      this.statusEl.textContent = ui(standing.key, this.lang);
    }
    /*
     * The map names too.
     *
     * Written once when the list was built, which meant they stayed in
     * whatever language the page opened in - so switching to English left
     * three Ukrainian names in the middle of an English screen, and the
     * check that reads every word on it said so.
     */
    for (const option of [...this.mapSelect.options]) {
      const map = MAPS.find((m) => m.id === option.value);
      if (map !== undefined) option.textContent = pick(map.name, this.lang);
    }
    this.draw();
  }

  private show(pane: NetworkPane, knock = true): void {
    this.pane = pane;
    this.statusEl.hidden = true;
    this.chosen = null;
    /*
     * The word is asked for when a world asks for it, and never before.
     *
     * On the making side it is always there, because that is where a word is
     * chosen rather than given - and leaving it empty is how a world is made
     * that anybody can walk into.
     */
    this.wordRow.hidden = pane === "join";
    this.worldsEl.hidden = true;
    this.draw();
    /*
     * Looking at what is there is not an act worth a button press.
     *
     * Except when somebody arrived from a reset message: they have one thing
     * to do, and a knock that fails would write "nobody home" over the
     * sentence telling them what it is.
     */
    if (knock && pane === "join" && this.ticket === null) void this.connect();
    if (pane === "create") void this.lookForKept();
    void this.refreshSignIn();
  }

  /**
   * Who is signed in, and a quiet try at signing in for a page that only
   * remembers a name and a password from before there was a dialog.
   */
  private async refreshSignIn(): Promise<void> {
    this.signedName = signedInAs(this.address());
    this.draw();
    if (this.signedName.length > 0) return;
    const name = savedName();
    const password = savedPassword();
    if (name.length === 0 || password.length === 0) return;
    const answer = await this.actions.account(this.address(), { t: "login", name, password });
    if (answer.ok && answer.token !== undefined) {
      keepNameTag(this.address(), "", name, answer.token);
      this.signedName = name;
      this.draw();
    }
  }

  private openLogin(): void {
    this.dialogRegisterMode(false);
    this.dialogName.value = savedName();
    this.dialogPass.value = "";
    this.dialogEmail.value = "";
    this.dialogStatus.hidden = true;
    this.dialogSaid = null;
    this.dialogEl.hidden = false;
    (this.dialogName.value.length > 0 ? this.dialogPass : this.dialogName).focus();
  }

  private closeLogin(): void {
    this.dialogEl.hidden = true;
  }

  private dialogRegisterMode(making: boolean): void {
    this.dialogMaking = making;
    this.dialogEmailRow.hidden = !making;
    this.dialogEmailNote.hidden = !making;
    this.retitle();
  }

  private dialogSay(key: UiKey, tone: "working" | "good" | "bad"): void {
    this.dialogSaid = key;
    this.dialogStatus.hidden = false;
    this.dialogStatus.textContent = ui(key, this.lang);
    this.dialogStatus.classList.toggle("good", tone === "good");
    this.dialogStatus.classList.toggle("bad", tone === "bad");
  }

  /** «Увійти», or «Створити» when the dialog is making an account. */
  private async dialogGoNow(): Promise<void> {
    if (this.busy) return;
    const name = this.dialogName.value.trim();
    const password = this.dialogPass.value;
    if (name.length === 0) { this.dialogSay("net-need-name", "bad"); this.dialogName.focus(); return; }
    this.busy = true;
    this.dialogSay("net-checking", "working");
    let answer: { ok: boolean; why: string; token?: string };
    try {
      answer = await this.actions.account(this.address(), this.dialogMaking
        ? {
          t: "register", name, password,
          ...(this.dialogEmail.value.trim().length > 0 ? { email: this.dialogEmail.value.trim() } : {}),
        }
        : { t: "login", name, password });
    } catch {
      answer = { ok: false, why: "unreachable" };
    } finally {
      this.busy = false;
    }
    if (!answer.ok || answer.token === undefined) {
      this.dialogSay(this.knownKey(answer.why), "bad");
      return;
    }
    // Signed in: the token is what the page comes back with from now on.
    keepNameTag(this.address(), "", name, answer.token);
    rememberName(name);
    rememberPassword(password);
    this.nameInput.value = name;
    this.passInput.value = password;
    this.signedName = name;
    this.closeLogin();
    this.statusEl.hidden = true;
    this.draw();
    void this.lookForKept();
  }

  private async dialogForgotNow(): Promise<void> {
    if (this.busy) return;
    const name = this.dialogName.value.trim();
    if (name.length === 0) { this.dialogSay("net-need-name", "bad"); this.dialogName.focus(); return; }
    this.busy = true;
    this.dialogSay("net-checking", "working");
    try {
      await this.actions.account(this.address(), { t: "forgot", name });
    } finally {
      this.busy = false;
    }
    this.dialogSay("net-reset-sent", "good");
  }

  private signOutNow(): void {
    signOut(this.address());
    this.signedName = "";
    this.statusEl.hidden = true;
    this.draw();
  }

  /**
   * Look for a world of theirs already here, and say so if there is one.
   *
   * Everything about it fails quiet: a browser that will not answer, or a
   * private window that keeps nothing, leaves the screen offering to make a
   * new world - which is what it offered before any of this existed.
   */
  private async lookForKept(): Promise<void> {
    const owner = this.nameInput.value.trim();
    let found: { map: string; savedAt: number } | null = null;
    try {
      found = await this.actions.kept(owner);
    } catch {
      found = null;
    }
    // They may have typed on while we were asking.
    if (this.nameInput.value.trim() !== owner) return;
    this.keptWorld = found;
    if (this.pane === "create") this.draw();
  }

  private draw(): void {
    const joining = this.pane === "join";
    this.joinButton.classList.toggle("active", joining);
    this.createButton.classList.toggle("active", !joining);
    /*
     * A line of explanation on the making side, and none on the joining one.
     *
     * "Введи адресу, яку дав той, хто підняв світ" said what the field under
     * it already says: there is one field, it is asking for an address, and
     * the person who opened «Приєднатись» knows why they are there. Making a
     * world is the half that genuinely needs a sentence - a world runs apart
     * from the page, which is not a thing anybody guesses.
     */
    /*
     * And a different sentence when their world is already here.
     *
     * Continuing is not making: the map, the seed and the word all come out
     * of what is written down, so offering to choose a map would be
     * offering a choice that is not taken. What is worth saying instead is
     * that it is the same world, with the same password, and when it was
     * last played.
     */
    const going = !joining && this.keptWorld !== null;
    this.blurbEl.hidden = joining;
    this.blurbEl.textContent = joining ? ""
      : going ? this.keptSentence()
      : ui("net-create-blurb", this.lang);
    // Which map, only when one is being made: joining is choosing from what
    // is already there, and continuing has already chosen.
    this.mapRow.hidden = joining || going;
    // The match's own two settings, on the one map that has them.
    this.warRow.hidden = joining || going ||
      mapById(this.mapSelect.value).terrain !== "war";
    // The word is the kept world's own; typing another would not change it.
    this.wordRow.hidden = joining || going;
    this.noteEl.hidden = joining;
    this.noteEl.textContent = joining ? ""
      : ui(going ? "net-create-note" : "net-create-word", this.lang);
    this.connectButton.textContent = ui(
      joining ? "net-refresh" : going ? "net-continue" : "net-start", this.lang);
    // Both are done signed in - "зроби таку саму логіку з логіном і для
    // опції Доєднатись". The fields stay only for a reset ticket, which
    // wants a new password typed; the sign-in row says who, on both panes;
    // and the button that makes a world sleeps until somebody has signed in.
    // The list can be looked at by anybody.
    const signed = this.signedName.length > 0;
    const resetting = this.ticket !== null;
    this.nameRow.hidden = true;
    this.passRow.hidden = !resetting;
    this.accountRow.hidden = true;
    this.signRow.hidden = resetting;
    this.signText.textContent = ui(signed ? "net-signed-as"
      : joining ? "net-join-login-needed" : "net-login-needed", this.lang);
    this.signName.textContent = signed ? this.signedName : "";
    this.signName.hidden = !signed;
    this.signButton.textContent = ui(signed ? "net-sign-out" : "net-login", this.lang);
    this.connectButton.disabled = !joining && !signed;
    this.againButton.hidden = !going;
    this.againButton.textContent = ui("net-start-again", this.lang);
  }

  /**
   * Throw the kept world away and offer a new one. Asked first: it is the
   * one button here that loses something, and cannot be taken back.
   */
  private async startAgain(): Promise<void> {
    if (this.busy || this.keptWorld === null) return;
    if (!window.confirm(ui("net-start-again-ask", this.lang))) return;
    this.busy = true;
    try {
      await this.actions.forget(this.nameInput.value.trim());
    } finally {
      this.busy = false;
    }
    this.keptWorld = null;
    this.statusEl.hidden = true;
    this.draw();
    this.say("net-started-again", "good");
  }

  /** "Your кремль is here, last played on Tuesday." */
  private keptSentence(): string {
    const kept = this.keptWorld;
    if (kept === null) return "";
    const map = pick(mapById(kept.map).name, this.lang);
    /*
     * The date in whatever the browser calls this language.
     *
     * Not formatted by hand: "востаннє грали 22.09" is one guess about how
     * somebody writes a date and wrong wherever they write it differently,
     * and the browser already knows.
     */
    const when = kept.savedAt > 0
      ? new Date(kept.savedAt).toLocaleDateString(
        this.lang === "uk" ? "uk-UA" : "en-GB",
        { day: "numeric", month: "long" })
      : "";
    return `${ui("net-continue-blurb", this.lang)} ${map}${when ? ` · ${when}` : ""}`;
  }

  private say(key: UiKey, tone: "working" | "good" | "bad"): void {
    // Kept, so that changing language re-words whatever is standing on the
    // screen: a sentence written once stays in the language it was written
    // in, and the check that reads every word on an English screen says so.
    this.said = { key, tone };
    this.statusEl.hidden = false;
    this.statusEl.textContent = ui(key, this.lang);
    this.statusEl.classList.toggle("good", tone === "good");
    this.statusEl.classList.toggle("bad", tone === "bad");
  }

  /** Where to talk to. One machine, the one this page came from. */
  private address(): string {
    return this.url;
  }

  private ticket: string | null = null;
  /** The last thing said on the status line, so it can be said again. */
  private said: { key: UiKey; tone: "working" | "good" | "bad" } | null = null;

  private async connect(): Promise<void> {
    if (this.busy) return;

    /*
     * Setting a new password, rather than joining anything.
     *
     * The same button, because it is the same act from where the person is
     * standing: they came here from a message and there is one thing to do.
     */
    if (this.ticket !== null) {
      const address = this.address();
      this.busy = true;
      this.say("net-checking", "working");
      const answer = await this.actions.account(address, {
        t: "reset", ticket: this.ticket, password: this.passInput.value,
      });
      this.busy = false;
      if (!answer.ok) { this.sayKey(answer.why); return; }
      // Done with, whatever happens next: a spent ticket is spent.
      this.ticket = null;
      this.registerButton.hidden = false;
      this.forgotButton.hidden = false;
      rememberPassword(this.passInput.value);
      this.retitle();
      this.say("net-reset-done", "good");
      return;
    }
    const address = this.address();

    /*
     * A name first, because there is nowhere to go without one.
     *
     * Checked here rather than at the world buttons: finding out that you
     * needed a name only after choosing which кремль to burn is a step
     * backwards for no reason.
     */
    // Making one is a different act from looking at what is there, and is
    // done as whoever signed in.
    if (this.pane === "create") {
      if (this.signedName.length === 0) { this.openLogin(); return; }
      this.nameInput.value = this.signedName;
      await this.makeWorld(address);
      return;
    }

    this.busy = true;
    this.say("net-checking", "working");
    this.worldsEl.hidden = true;
    this.worldsEl.replaceChildren();
    let answer: ProbeAnswer;
    try {
      answer = await this.actions.probe(address, this.wordInput.value);
    } catch {
      answer = { verdict: "unreachable", worlds: [] };
    } finally {
      this.busy = false;
    }

    const { verdict, worlds } = answer;
    if (verdict === "unreachable") { this.say("net-unreachable", "bad"); return; }
    if (verdict === "wrong-word") { this.say("net-wrong-word", "bad"); return; }
    if (verdict === "mismatch") { this.say("net-mismatch", "bad"); return; }

    /*
     * The list first, and the word after.
     *
     * The word belongs to the world, so the knock above could not have
     * checked it - there was nothing to check it against until a world was
     * named. So the list always comes up, even for one world: choosing is
     * what says which door the word is for.
     */
    if (worlds.length === 0) { this.say("net-unreachable", "bad"); return; }
    if (this.trouble !== null) {
      this.say(this.trouble, "bad");
      this.trouble = null;
    } else {
      this.say("net-pick-world", "good");
    }
    this.showWorlds(address, worlds);
  }

  /**
   * A row for each world the server has, with a padlock where there is one.
   *
   * Clicking a row with no padlock goes straight in - there is nothing to
   * ask. Clicking one with a padlock puts the word field under the cursor
   * and waits, because being asked for a word you were never told is the
   * only thing about this screen that can go wrong for somebody who did
   * everything right.
   */
  private showWorlds(url: string, worlds: ProbeAnswer["worlds"]): void {
    this.worldsEl.replaceChildren(...worlds.map((world) => {
      // A row with a button on it - "додай кнопку доєднатись у кожну гру
      // в списку" - and the row itself does the same, since it always did.
      // Neither goes anywhere before somebody has signed in.
      const go = (): void => {
        if (this.signedName.length === 0) { this.openLogin(); return; }
        if (world.locked === true && this.wordInput.value.trim().length === 0) {
          this.chosen = { id: world.id, guest: world.guest === true };
          this.wordRow.hidden = false;
          this.say("net-need-word", "bad");
          this.wordInput.focus();
          return;
        }
        void this.enter(url, world.id, world.guest === true);
      };
      const el = document.createElement("div");
      el.className = "net-world";
      el.addEventListener("click", go);
      const text = document.createElement("div");
      text.className = "net-world-text";
      const join = button("net-world-join", () => go());
      join.textContent = ui("net-join-world", this.lang);
      join.addEventListener("click", (e) => e.stopPropagation());
      /*
       * The map's name, and then whose world it is.
       *
       * "Створені світи на одній мапі виглядають однаково для того, хто
       * хоче приєднатись" - and they did: two «Запалити кремлін» in a row,
       * the same name and the same sentence under it, and nothing to choose
       * between them. The name of whoever made it is the thing that tells
       * them apart, so it goes where the eye already is.
       *
       * A world the machine itself put up has no maker: those get the name
       * the operator gave it, which is what it is called in the log and on
       * the command line, and is still a name rather than two identical
       * lines.
       */
      const name = document.createElement("strong");
      const whose = world.owner ?? world.id;
      name.textContent = `${pick(world.name, this.lang)} · ${whose}`;
      const blurb = document.createElement("span");
      blurb.className = "net-world-blurb";
      /*
       * How many are in it, and whether it asks for a password.
       *
       * "Somebody is already there" is most of what anybody wants to know
       * when choosing between two of them.
       */
      const parts = [pick(world.blurb, this.lang)];
      if (world.players > 0) parts.push(String(world.players));
      /*
       * And whether it is somebody's own computer.
       *
       * Worth a word on the row rather than a surprise afterwards: a world
       * held in a browser goes when that browser does, and how well it runs
       * is how good somebody's machine and line are - neither of which is
       * true of the worlds this server holds itself.
       */
      if (world.guest === true) parts.push(ui("net-guest-world", this.lang));
      // Whose it is has moved up beside the name - see above.
      if (world.locked === true) parts.push(ui("net-locked", this.lang));
      blurb.textContent = parts.join(" · ");
      text.append(name, blurb);
      el.append(text, join);
      return el;
    }));
    this.worldsEl.hidden = worlds.length === 0;
  }

  /**
   * Hold a world of the chosen map on this computer, and walk into it.
   *
   * «Створити» has been three different things. First it printed a command
   * for you to run in a terminal; then our server made the world and you
   * joined it; now it is yours to hold, and our machine does two things
   * about it - puts a row in the list so people find it, and introduces
   * them. Every game after that goes straight between their computer and
   * this one, which is what stops the number of worlds anybody can play in
   * being a question about our machine.
   *
   * Nothing is made here. The world is grown at startup, and joining is a
   * reload, so a world made on this screen would die on the way to itself:
   * what crosses the reload is the intention, and the page on the other side
   * makes the world it is going to keep.
   */
  private async makeWorld(url: string): Promise<void> {
    // Asked first. Holding needs an account, and finding that out after the
    // reload put the player back on the home screen with an eight-second
    // notice - "замість початку гри мене редіректить на головну".
    this.busy = true;
    this.say("net-checking", "working");
    let taken: { ok: boolean; why: string };
    try {
      taken = await this.actions.mayHost(
        url, this.nameInput.value.trim(), this.passInput.value);
    } catch {
      taken = { ok: false, why: "net-unreachable" };
    } finally {
      this.busy = false;
    }
    if (!taken.ok) {
      // A token the server has stopped knowing: back to the sign-in row.
      if (taken.why === "net-sign-in") {
        signOut(url);
        this.signedName = "";
        this.draw();
        this.say("net-session-stale", "bad");
        return;
      }
      this.say(taken.why as UiKey, "bad");
      return;
    }
    rememberName(this.nameInput.value.trim());
    rememberPassword(this.passInput.value);
    this.say("net-made", "good");
    // The match's two settings, on the map that has them - they were drawn
    // and never sent, so every war held in a browser was without bots.
    const war = mapById(this.mapSelect.value).terrain === "war";
    const minutes = Math.min(60, Math.max(1, Number(this.prepInput.value) || 10));
    this.actions.hold(url, this.mapSelect.value, this.wordInput.value.trim(),
      war ? {
        bots: this.botsToggle.checked, prep: minutes * 60,
        hardcore: this.hardcoreToggle.checked,
        limit: Math.min(MAX_MATCH_HOURS, Math.max(1, Number(this.hoursInput.value) || MATCH_HOURS)) * 3600,
      } : undefined);
  }

  /**
   * Try that world's word, and go in if it opens.
   *
   * Asked before the reload rather than after, because joining *is* a reload
   * - so a mistyped word would otherwise be a world grown for somebody who
   * is not coming in, and a blank failure on the far side of a page load.
   */
  private async enter(url: string, world: string, guest = false): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.say("net-checking", "working");
    let answer: ProbeAnswer;
    try {
      /*
       * A world on somebody's computer is not on our server to look into:
       * asked about by name, the server said "no such world" and the page
       * called that a version mismatch. The knock happens on the way in,
       * through the switchboard, and the host's own page checks the word;
       * here only the server's being there is asked.
       */
      answer = guest
        ? await this.actions.probe(url, "")
        : await this.actions.probe(url, this.wordInput.value, world);
    } catch {
      answer = { verdict: "unreachable", worlds: [] };
    } finally {
      this.busy = false;
    }
    const refusal: Partial<Record<ProbeAnswer["verdict"], UiKey>> = {
      "wrong-word": "net-wrong-word", mismatch: "net-mismatch",
      unreachable: "net-unreachable", gone: "net-gone", full: "net-full",
      late: "net-late", "name-taken": "net-taken-name",
    };
    const why = refusal[answer.verdict];
    if (why !== undefined) { this.say(why, "bad"); return; }
    this.say("net-joining", "good");
    // Under the name that signed in, which is the one the world keeps a
    // bag against.
    rememberName(this.signedName);
    // The page reloads into that world - see platform/net.ts for why joining
    // is a reload rather than a swap in flight.
    this.actions.join(url, this.wordInput.value, world, guest);
  }

  /**
   * Make an account, in two presses.
   *
   * The first opens the address field and changes what the button says; the
   * second sends it. One press would either hide the address entirely or
   * show a field nobody joining an ordinary world ever wants to look at.
   */
  private async register(): Promise<void> {
    if (this.busy) return;
    if (!this.making) {
      this.making = true;
      this.emailRow.hidden = false;
      this.emailNote.hidden = false;
      this.retitle();
      this.nameInput.focus();
      return;
    }
    const address = this.address();
    this.busy = true;
    this.say("net-checking", "working");
    const answer = await this.actions.account(address, {
      t: "register",
      name: this.nameInput.value.trim(),
      password: this.passInput.value,
      ...(this.emailInput.value.trim().length > 0
        ? { email: this.emailInput.value.trim() } : {}),
    });
    this.busy = false;
    if (!answer.ok) { this.sayKey(answer.why); return; }
    // Kept, so the next press of «З'єднатись» is already them.
    rememberName(this.nameInput.value.trim());
    rememberPassword(this.passInput.value);
    this.making = false;
    this.emailRow.hidden = true;
    this.emailNote.hidden = true;
    this.retitle();
    this.say("net-account-made", "good");
  }

  /**
   * Ask for a way back in.
   *
   * The answer is the same whoever asks and whatever happens - see `forgot`
   * in the protocol. The screen therefore says one thing, which is the
   * honest sentence rather than a promise.
   */
  private async forgot(): Promise<void> {
    if (this.busy) return;
    const address = this.address();
    if (this.nameInput.value.trim().length === 0) {
      this.say("net-need-name", "bad");
      return;
    }
    this.busy = true;
    this.say("net-checking", "working");
    await this.actions.account(address, {
      t: "forgot", name: this.nameInput.value.trim(),
    });
    this.busy = false;
    this.say("net-reset-sent", "good");
  }

  /**
   * Say something the server named, in the language on this screen.
   *
   * The server sends keys rather than sentences, so nothing at the far end
   * has to know which language anybody is reading. An unknown key falls back
   * to the general refusal rather than to a blank line.
   */
  private sayKey(why: string): void {
    this.say(this.knownKey(why), "bad");
  }

  private knownKey(why: string): UiKey {
    const known: Record<string, UiKey> = {
      "pass-short": "net-pass-short",
      "pass-long": "net-pass-long",
      "name-empty": "net-need-name",
      "name-long": "net-name-long",
      "name-odd": "net-name-odd",
      "name-taken": "net-name-taken",
      "email-odd": "net-email-odd",
      "reset-done": "net-reset-done",
      "reset-stale": "net-reset-stale",
      "sign-in": "net-sign-in",
      "your-worlds-full": "net-your-worlds-full",
      "server-full": "net-server-full",
      "no-such-map": "net-no-such-map",
      "wrong": "net-wrong-login",
    };
    return known[why] ?? "net-unreachable";
  }

  /** What the client speaks, so the panel can say so if it ever differs. */
  get version(): number {
    return PROTOCOL_VERSION;
  }
}

function button(className: string, onClick: () => void): HTMLButtonElement {
  const el = document.createElement("button");
  el.type = "button";
  el.className = className;
  el.addEventListener("click", onClick);
  return el;
}
